import { useEffect, useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  Calendar as RbcCalendar,
  momentLocalizer,
  type Event as RbcEvent,
  type View as RbcView,
  type SlotInfo,
} from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './calendar-captations.calendar.css';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import { CalendarPlus, Search } from 'lucide-react';
import { format, startOfDay, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import {
  captationService,
  CaptationRequest,
  UpdateCaptationDto,
} from '@/features/captation/services/captation.service';
import { CaptationRequestDialog } from '@/features/captation/components/CaptationRequestDialog';
import { calendarService, CalendarEvent } from '@/features/calendar/services/calendar.service';
import { EventModal } from '@/features/calendar/components/EventModal';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useConfirm } from '@/components/ui/confirm-dialog';

const rbcLocalizer = momentLocalizer(moment);

export const Route = createFileRoute('/_authenticated/calendar-captations')({
  component: CalendarCaptationsPage,
});

function CalendarCaptationsPage() {
  return (
    <PermissionGate permission={Permission.CALENDAR_VIEW}>
      <CalendarCaptationsContent />
    </PermissionGate>
  );
}

function CalendarCaptationsContent() {
  const { toast } = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState<CaptationRequest | null>(null);
  const [selectedDay, setSelectedDay] = useState(() => startOfDay(new Date()));
  const [calendarDate, setCalendarDate] = useState(() => new Date());
  const [calView, setCalView] = useState<RbcView>('month');

  const { data: allCaptations = [], isLoading: loadingAll } = useQuery({
    queryKey: ['captation-list'],
    queryFn: () => captationService.list(),
  });

  const { data: calendarCaptacaoEvents = [], isLoading: loadingCalendar } = useQuery({
    queryKey: ['calendar-captacao-events'],
    queryFn: () => calendarService.getCaptacaoEvents(),
  });

  type CaptationRow =
    | { type: 'request'; data: CaptationRequest }
    | { type: 'agenda'; data: CalendarEvent };
  const allCaptationsWithAgenda = useMemo((): CaptationRow[] => {
    const fromRequests: CaptationRow[] = (allCaptations as CaptationRequest[]).map(
      (r) => ({ type: 'request' as const, data: r }),
    );
    const fromAgenda: CaptationRow[] = calendarCaptacaoEvents.map((e) => ({
      type: 'agenda' as const,
      data: e,
    }));
    const merged = [...fromRequests, ...fromAgenda];
    merged.sort((a, b) => {
      const dateA =
        a.type === 'request'
          ? new Date(a.data.scheduledAt).getTime()
          : new Date(a.data.startTime).getTime();
      const dateB =
        b.type === 'request'
          ? new Date(b.data.scheduledAt).getTime()
          : new Date(b.data.startTime).getTime();
      return dateB - dateA;
    });
    return merged;
  }, [allCaptations, calendarCaptacaoEvents]);

  const allCaptationsFiltered = useMemo(() => {
    if (!searchTerm.trim()) return allCaptationsWithAgenda;
    const q = searchTerm.trim().toLowerCase();
    return allCaptationsWithAgenda.filter((row) => {
      if (row.type === 'request') {
        const r = row.data as CaptationRequest;
        const name = (r.name ?? '').toLowerCase();
        const message = (r.message ?? '').toLowerCase();
        const emails = (r.emails ?? []).join(' ').toLowerCase();
        return name.includes(q) || message.includes(q) || emails.includes(q);
      }
      const e = row.data as CalendarEvent;
      const title = (e.title ?? '').toLowerCase();
      const desc = (e.description ?? '').toLowerCase();
      return title.includes(q) || desc.includes(q);
    });
  }, [allCaptationsWithAgenda, searchTerm]);

  const { data: myCaptations = [], isLoading: loadingMy } = useQuery({
    queryKey: ['captation-my'],
    queryFn: () => captationService.listMy(),
  });

  const myCaptationsSorted = useMemo(
    () =>
      [...myCaptations].sort(
        (a, b) =>
          new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime(),
      ),
    [myCaptations],
  );

  const myCaptationsFiltered = useMemo(() => {
    if (!searchTerm.trim()) return myCaptationsSorted;
    const q = searchTerm.trim().toLowerCase();
    return myCaptationsSorted.filter((r) => {
      const name = (r.name ?? '').toLowerCase();
      const message = (r.message ?? '').toLowerCase();
      const emails = (r.emails ?? []).join(' ').toLowerCase();
      return name.includes(q) || message.includes(q) || emails.includes(q);
    });
  }, [myCaptationsSorted, searchTerm]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => captationService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['captation-list'] });
      queryClient.invalidateQueries({ queryKey: ['captation-my'] });
      toast({
        title: 'Solicitação excluída',
        description: 'A solicitação foi removida com sucesso.',
      });
    },
  });

  const openEditDialog = (request: CaptationRequest) => {
    setEditingRequest(request);
    setEditOpen(true);
  };

  const handleDelete = async (request: CaptationRequest) => {
    if (request.status !== 'pending') {
      toast({
        title: 'Solicitação já processada',
        description: 'Apenas solicitações pendentes podem ser excluídas.',
        variant: 'destructive',
      });
      return;
    }

    const confirmed = await confirm({
      title: `Excluir a solicitação "${request.name}"?`,
      description: 'Esta ação não pode ser desfeita.',
      confirmLabel: 'Excluir solicitação',
      destructive: true,
    });
    if (!confirmed) return;
    deleteMutation.mutate(request.id);
  };

  const captationRbcEvents = useMemo((): RbcEvent[] => {
    return allCaptationsFiltered.map((row) => {
      if (row.type === 'request') {
        const r = row.data;
        const start = new Date(r.scheduledAt);
        const end = new Date(start.getTime() + 30 * 60 * 1000);
        return {
          id: `req-${r.id}`,
          title: r.name,
          start,
          end,
          resource: row,
        };
      }
      const e = row.data;
      return {
        id: `evt-${e.id}`,
        title: e.title,
        start: new Date(e.startTime),
        end: new Date(e.endTime),
        resource: row,
      };
    });
  }, [allCaptationsFiltered]);

  const dayCaptations = useMemo(() => {
    return allCaptationsFiltered
      .filter((row) => {
        const t =
          row.type === 'request'
            ? new Date(row.data.scheduledAt)
            : new Date(row.data.startTime);
        return isSameDay(t, selectedDay);
      })
      .sort((a, b) => {
        const ta =
          a.type === 'request'
            ? new Date(a.data.scheduledAt).getTime()
            : new Date(a.data.startTime).getTime();
        const tb =
          b.type === 'request'
            ? new Date(b.data.scheduledAt).getTime()
            : new Date(b.data.startTime).getTime();
        return ta - tb;
      });
  }, [allCaptationsFiltered, selectedDay]);

  const handleSelectCalendarSlot = (slot: SlotInfo) => {
    const raw =
      Array.isArray(slot.slots) && slot.slots.length > 0 ? slot.slots[0] : slot.start;
    const d = raw instanceof Date ? raw : new Date(raw);
    setSelectedDay(startOfDay(d));
    setCalendarDate(d);
  };

  const handleSelectCalendarEvent = (event: RbcEvent) => {
    const start = event.start ?? new Date();
    setSelectedDay(startOfDay(start));
    setCalendarDate(start);
    const row = event.resource as CaptationRow | undefined;
    if (row?.type === 'agenda') {
      setSelectedEventId(row.data.id);
      setEventModalOpen(true);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl font-bold">Calendário de Captações</h1>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome, mensagem ou e-mail..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button onClick={() => setFormOpen(true)}>
            <CalendarPlus className="mr-2 h-4 w-4" />
            Solicitar Captação
          </Button>
        </div>
      </div>

      <Tabs defaultValue="calendar" className="space-y-4">
        <TabsList>
          <TabsTrigger value="calendar">Calendário</TabsTrigger>
          <TabsTrigger value="all">Captações agendadas</TabsTrigger>
          <TabsTrigger value="my">Minhas solicitações</TabsTrigger>
        </TabsList>
        <TabsContent value="calendar" className="space-y-4">
          <div className="flex flex-col gap-4 xl:flex-row">
            <Card className="min-h-0 flex-1">
              <CardContent className="p-3 md:p-4">
                {loadingAll || loadingCalendar ? (
                  <div className="flex h-[520px] items-center justify-center text-muted-foreground">
                    Carregando...
                  </div>
                ) : (
                  <div className="h-[65vh] min-h-[520px]">
                    <RbcCalendar
                      className={cn(
                        'calendar-captations-rbc',
                        calView === 'month' && 'calendar-captations-month',
                      )}
                      localizer={rbcLocalizer}
                      events={captationRbcEvents}
                      startAccessor="start"
                      endAccessor="end"
                      view={calView}
                      onView={setCalView}
                      date={calendarDate}
                      onNavigate={setCalendarDate}
                      selectable
                      popup
                      onSelectSlot={handleSelectCalendarSlot}
                      onSelectEvent={handleSelectCalendarEvent}
                      style={{ height: '100%' }}
                      components={{
                        event: CaptationCalendarEvent,
                      }}
                      messages={{
                        next: 'Próximo',
                        previous: 'Anterior',
                        today: 'Hoje',
                        month: 'Mês',
                        week: 'Semana',
                        day: 'Dia',
                        agenda: 'Agenda',
                      }}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="xl:w-[360px] xl:flex-shrink-0">
              <CardContent className="space-y-3 p-4">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="font-semibold">Agenda do dia</h2>
                  <Badge variant="secondary">{dayCaptations.length}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {format(selectedDay, "EEEE, dd 'de' MMMM 'de' yyyy", {
                    locale: ptBR,
                  })}
                </p>
                <div className="max-h-[60vh] space-y-2 overflow-auto pr-1">
                  {loadingAll || loadingCalendar ? (
                    <p className="text-sm text-muted-foreground">Carregando...</p>
                  ) : dayCaptations.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Nenhuma captação neste dia.
                    </p>
                  ) : (
                    dayCaptations.map((row) =>
                      row.type === 'request' ? (
                        <div
                          key={`day-req-${row.data.id}`}
                          className="w-full rounded-md border p-2"
                        >
                          <p className="text-sm font-medium break-words whitespace-normal">
                            {row.data.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {format(
                              new Date(row.data.scheduledAt),
                              "dd/MM/yyyy 'às' HH:mm",
                              { locale: ptBR },
                            )}
                          </p>
                          <Badge className="mt-1" variant="secondary">
                            Solicitação
                          </Badge>
                        </div>
                      ) : (
                        <button
                          key={`day-evt-${row.data.id}`}
                          type="button"
                          className="w-full rounded-md border p-2 text-left transition-colors hover:bg-muted"
                          onClick={() => {
                            setSelectedEventId(row.data.id);
                            setEventModalOpen(true);
                          }}
                        >
                          <p className="text-sm font-medium break-words whitespace-normal">
                            {row.data.title}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {format(
                              new Date(row.data.startTime),
                              "dd/MM/yyyy 'às' HH:mm",
                              { locale: ptBR },
                            )}
                          </p>
                          <Badge className="mt-1" variant="outline">
                            Agenda
                          </Badge>
                        </button>
                      ),
                    )
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="all" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Captações agendadas (solicitações + agenda)</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingAll || loadingCalendar ? (
                <p className="text-muted-foreground">Carregando...</p>
              ) : allCaptationsFiltered.length === 0 ? (
                <p className="text-muted-foreground">
                  {searchTerm.trim()
                    ? 'Nenhum resultado para a busca.'
                    : 'Nenhuma captação agendada.'}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Data e hora</TableHead>
                      <TableHead>Origem</TableHead>
                      <TableHead>Solicitante</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allCaptationsFiltered.map((row) =>
                      row.type === 'request' ? (
                        <TableRow key={`req-${row.data.id}`}>
                          <TableCell className="font-medium">
                            {row.data.name}
                          </TableCell>
                          <TableCell>
                            {format(
                              new Date(row.data.scheduledAt),
                              "dd/MM/yyyy 'às' HH:mm",
                              { locale: ptBR },
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">Solicitação</Badge>
                          </TableCell>
                          <TableCell>
                            {row.data.requestedByUser?.email ?? row.data.requestedBy}
                          </TableCell>
                          <TableCell>
                            <StatusBadge
                              status={row.data.status}
                              rejectionReason={row.data.rejectionReason}
                            />
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow
                          key={`evt-${row.data.id}`}
                          className="cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => {
                            setSelectedEventId(row.data.id);
                            setEventModalOpen(true);
                          }}
                        >
                          <TableCell className="font-medium">
                            {row.data.title}
                          </TableCell>
                          <TableCell>
                            {format(
                              new Date(row.data.startTime),
                              "dd/MM/yyyy 'às' HH:mm",
                              { locale: ptBR },
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">Agenda</Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            —
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            —
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="my" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Minhas solicitações</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingMy ? (
                <p className="text-muted-foreground">Carregando...</p>
              ) : myCaptationsFiltered.length === 0 ? (
                <p className="text-muted-foreground">
                  {searchTerm.trim()
                    ? 'Nenhum resultado para a busca.'
                    : 'Você ainda não criou nenhuma solicitação.'}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Data e hora</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {myCaptationsFiltered.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium">{r.name}</TableCell>
                        <TableCell>
                          {format(
                            new Date(r.scheduledAt),
                            "dd/MM/yyyy 'às' HH:mm",
                            { locale: ptBR },
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusBadge
                            status={r.status}
                            rejectionReason={r.rejectionReason}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDialog(r)}
                              disabled={r.status !== 'pending'}
                            >
                              Editar
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDelete(r)}
                              disabled={r.status !== 'pending' || deleteMutation.isPending}
                            >
                              Excluir
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <CaptationRequestDialog open={formOpen} onOpenChange={setFormOpen} />

      <EditMyCaptationDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        request={editingRequest}
      />

      {selectedEventId && (
        <EventModal
          eventId={selectedEventId}
          open={eventModalOpen}
          onOpenChange={setEventModalOpen}
        />
      )}
    </div>
  );
}

function CaptationCalendarEvent({ title }: { title?: string }) {
  return <span className="block break-words whitespace-normal">{title}</span>;
}

function toInputDateTime(iso: string) {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  const hour = String(parsed.getHours()).padStart(2, '0');
  const minute = String(parsed.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

export function EditMyCaptationDialog({
  open,
  onOpenChange,
  request,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: CaptationRequest | null;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<UpdateCaptationDto>({
    name: '',
    message: '',
    scheduledAt: '',
    emails: [''],
  });

  useEffect(() => {
    if (!request || !open) return;
    setForm({
      notionBoardId: request.notionBoardId ?? undefined,
      name: request.name,
      message: request.message ?? '',
      scheduledAt: toInputDateTime(request.scheduledAt),
      emails: request.emails?.length ? [...request.emails] : [''],
    });
  }, [request, open]);

  const updateMutation = useMutation({
    mutationFn: (payload: UpdateCaptationDto) => {
      if (!request) throw new Error('Solicitação inválida');
      return captationService.update(request.id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['captation-list'] });
      queryClient.invalidateQueries({ queryKey: ['captation-my'] });
      onOpenChange(false);
      toast({
        title: 'Solicitação atualizada',
        description: 'As alterações foram salvas com sucesso.',
      });
    },
  });

  if (!request) return null;

  const addEmail = () =>
    setForm((current) => ({
      ...current,
      emails: [...(current.emails ?? []), ''],
    }));

  const removeEmail = (index: number) =>
    setForm((current) => ({
      ...current,
      emails: (current.emails ?? []).filter((_, i) => i !== index),
    }));

  const setEmail = (index: number, value: string) =>
    setForm((current) => ({
      ...current,
      emails: (current.emails ?? []).map((email, i) =>
        i === index ? value : email,
      ),
    }));

  const handleSave = () => {
    const emails = (form.emails ?? []).map((e) => e.trim()).filter(Boolean);
    if (!form.name?.trim()) {
      toast({
        title: 'Campo obrigatório',
        description: 'Informe o nome da captação.',
        variant: 'destructive',
      });
      return;
    }
    if (!form.scheduledAt) {
      toast({
        title: 'Campo obrigatório',
        description: 'Informe data e hora.',
        variant: 'destructive',
      });
      return;
    }
    if (emails.length === 0) {
      toast({
        title: 'Campo obrigatório',
        description: 'Informe pelo menos um e-mail.',
        variant: 'destructive',
      });
      return;
    }

    updateMutation.mutate({
      notionBoardId: form.notionBoardId,
      name: form.name.trim(),
      message: form.message ?? '',
      scheduledAt: new Date(form.scheduledAt).toISOString(),
      emails,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" busy={updateMutation.isPending}>
        <DialogHeader>
          <DialogTitle>Editar solicitação</DialogTitle>
        </DialogHeader>
        <form
          className="grid gap-4 py-2"
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <div className="grid gap-2">
            <label htmlFor="cap-name" className="text-sm font-medium">Nome da captação *</label>
            <Input
              id="cap-name"
              value={form.name ?? ''}
              onChange={(e) =>
                setForm((current) => ({ ...current, name: e.target.value }))
              }
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium">Mensagem</label>
            <Textarea
              value={form.message ?? ''}
              onChange={(e) =>
                setForm((current) => ({ ...current, message: e.target.value }))
              }
              rows={3}
            />
          </div>
          <div className="grid gap-2">
            <label htmlFor="cap-when" className="text-sm font-medium">Data e hora *</label>
            <Input
              id="cap-when"
              type="datetime-local"
              value={form.scheduledAt ?? ''}
              onChange={(e) =>
                setForm((current) => ({
                  ...current,
                  scheduledAt: e.target.value,
                }))
              }
            />
          </div>
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">E-mails</label>
              <Button type="button" size="sm" variant="outline" onClick={addEmail}>
                Adicionar
              </Button>
            </div>
            {(form.emails ?? []).map((email, index) => (
              // shortcut: key por índice; ok porque remover reindexa e o valor é controlado
              <div key={index} className="flex items-center gap-2">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(index, e.target.value)}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => removeEmail(index)}
                  disabled={(form.emails ?? []).length <= 1}
                  aria-label={`Remover e-mail ${index + 1}`}
                >
                  Remover
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={updateMutation.isPending}>
              Salvar alterações
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StatusBadge({
  status,
  rejectionReason,
}: {
  status: string;
  rejectionReason?: string | null;
}) {
  const map: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    pending: { label: 'Pendente', variant: 'secondary' },
    approved: { label: 'Aprovada', variant: 'default' },
    rejected: { label: 'Rejeitada', variant: 'destructive' },
  };
  const c = map[status] ?? { label: status, variant: 'outline' as const };
  return (
    <div className="space-y-1 max-w-xs">
      <Badge variant={c.variant}>{c.label}</Badge>
      {status === 'rejected' && rejectionReason && (
        <p className="text-xs text-muted-foreground leading-snug whitespace-pre-line">
          Motivo: {rejectionReason}
        </p>
      )}
    </div>
  );
}
